import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

const SYMBOLS = ["🎁", "💎", "⭐", "🍀", "🏆", "🔔", "🎉"];
const SYMBOL_INITIAL = { scale: 0.4, opacity: 0 };
const SYMBOL_ANIMATE = { scale: 1, opacity: 1 };
const SYMBOL_TRANSITION = { type: "spring", stiffness: 300, damping: 12 };
const GAME_INITIAL = { opacity: 0, scale: 0.92 };
const GAME_ANIMATE = { opacity: 1, scale: 1 };
const LIGHTS = Array.from({ length: 22 }, (_, i) => i);
const STOP_AT = 2200;
const STOP_GAP = 260;
const spinLabel = (spinning, win) => {
  if (spinning) return "Estrazione in corso…";
  return win ? "🎊 Combinazione vincente!" : "Estrazione completata";
};

const Symbol = ({ s, big }) => s?.img ? (
  <img src={s.img} alt={s.label || ""} className={`${big ? "h-[70%] w-[82%]" : "h-[60%] w-[80%]"} rounded-xl object-contain drop-shadow-[0_6px_14px_rgba(0,0,0,.6)]`} draggable={false} />
) : <span>{s?.emoji || s}</span>;

function Reel({ spinning, finalSymbol, strip, delay }) {
  return (
    <div className="relative h-36 w-28 sm:h-56 sm:w-44 md:h-64 md:w-52 rounded-3xl bg-gradient-to-b from-slate-800 via-slate-950 to-slate-800 border-[6px] border-amber-400 overflow-hidden grid place-items-center shadow-[inset_0_0_40px_rgba(0,0,0,.8),0_0_30px_rgba(251,191,36,.45)]" data-testid="slot-reel">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-black/70 to-transparent z-10" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/70 to-transparent z-10" />
      <div className="pointer-events-none absolute inset-x-2 top-1/2 h-0.5 -translate-y-1/2 bg-amber-300/60 z-10" />
      {spinning ? (
        <div className="reel-strip reel-spinning" style={{ animationDelay: `${delay}ms` }}>
          {[...strip, ...strip].map((s, i) => (
            <div key={`${i}-${s.label || s.emoji}`} className="h-36 sm:h-56 md:h-64 grid place-items-center text-6xl sm:text-8xl md:text-9xl">
              <Symbol s={s} />
            </div>
          ))}
        </div>
      ) : (
        <motion.div initial={SYMBOL_INITIAL} animate={SYMBOL_ANIMATE} transition={SYMBOL_TRANSITION} className="grid h-full w-full place-items-center text-7xl sm:text-8xl md:text-9xl drop-shadow-[0_0_18px_rgba(255,255,255,.6)]">
          <Symbol s={finalSymbol} big />
        </motion.div>
      )}
    </div>
  );
}

function buildStrip(prizes) {
  const withImg = (prizes || []).filter((p) => p.image_url).map((p) => ({ img: p.image_url, label: p.name }));
  const base = SYMBOLS.map((e) => ({ emoji: e, label: e }));
  if (!withImg.length) return base;
  const out = [];
  for (let i = 0; i < Math.max(7, withImg.length * 2); i++) out.push(i % 2 === 0 ? withImg[(i / 2) % withImg.length] : base[i % base.length]);
  return out;
}

export default function InstantWinGame({ result, onComplete, prizes, winPrize, sounds }) {
  const [stopped, setStopped] = useState(0);
  const win = result === "win";
  const strip = useMemo(() => buildStrip(prizes), [prizes]);
  const finals = useMemo(() => {
    const won = (prizes || []).find((p) => p.name === winPrize && p.image_url) || (winPrize === "Premio di esempio" ? (prizes || []).find((p) => p.image_url) : null);
    const winSym = won ? { img: won.image_url, label: won.name } : { emoji: "🏆", label: "🏆" };
    if (win) return [winSym, winSym, winSym];
    const pool = strip.filter((s) => s.label !== winSym.label);
    return [pool[0], pool[1 % pool.length], pool[2 % pool.length]];
  }, [prizes, winPrize, win, strip]);

  useEffect(() => {
    sounds?.play?.("reel_tick");
    const timers = [0, 1, 2].map((i) => setTimeout(() => { setStopped(i + 1); sounds?.play?.("reel_stop"); }, STOP_AT + i * STOP_GAP));
    const tJack = setTimeout(() => { if (win) sounds?.play?.("jackpot"); }, STOP_AT + 2 * STOP_GAP + 150);
    const tDone = setTimeout(() => onComplete(), STOP_AT + 2 * STOP_GAP + (win ? 1500 : 1000));
    return () => { timers.forEach(clearTimeout); clearTimeout(tJack); clearTimeout(tDone); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onComplete]);

  const spinning = stopped < 3;
  return (
    <motion.div initial={GAME_INITIAL} animate={GAME_ANIMATE} className="relative mx-auto w-full max-w-4xl rounded-[2.5rem] bg-slate-950/70 backdrop-blur-md border-4 border-amber-400/80 px-5 py-8 sm:px-10 sm:py-12 text-center text-white shadow-[0_0_80px_rgba(251,191,36,.35)]" data-testid="instant-win-game">
      <div className="pointer-events-none absolute inset-3 rounded-[2rem] border border-white/10" />
      <div className="pointer-events-none absolute -inset-1 flex flex-wrap justify-between px-4" aria-hidden="true">
        {LIGHTS.map((i) => <span key={i} className={`slot-light h-2.5 w-2.5 rounded-full ${!spinning && win ? "bg-emerald-300" : "bg-amber-300"}`} style={{ animationDelay: `${(i % 6) * 120}ms`, animationDuration: !spinning && win ? ".35s" : "1s" }} />)}
      </div>
      <p className="font-display font-extrabold text-2xl sm:text-4xl uppercase tracking-wide text-amber-300 drop-shadow mb-6 sm:mb-10" data-testid="instant-win-label">
        {spinLabel(spinning, win)}
      </p>
      <div className="flex items-center justify-center gap-3 sm:gap-6">
        {[0, 1, 2].map((i) => <Reel key={i} spinning={stopped <= i} finalSymbol={finals[i]} strip={strip} delay={i * 120} />)}
      </div>
      {!spinning && win && finals[0]?.img && <p className="mt-6 font-display text-xl sm:text-3xl font-bold text-white drop-shadow" data-testid="instant-win-prize">{finals[0].label}</p>}
      <div className="mx-auto mt-8 h-3 w-2/3 rounded-full bg-gradient-to-r from-transparent via-amber-400/80 to-transparent" />
    </motion.div>
  );
}
